import { Link, usePage } from "@inertiajs/react";
import {
  ChartPieIcon,
  ChevronsUpDown,
  CreditCard,
  EqualApproximately,
  Landmark,
  Layers,
  ListTodoIcon,
  LogOut,
  ReceiptText,
  UserIcon,
  Users,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User } from "@/types";

type Props = {
  user: User;
  draft_invoices_count: number;
};

export function AppSidebar({ user, draft_invoices_count }: Props) {
  return (
    <Sidebar collapsible="icon">
      <AppSidebarHeader />
      <AppSidebarContent draft_invoices_count={draft_invoices_count} />
      <AppSidebarFooter user={user} />
      <SidebarRail />
    </Sidebar>
  );
}

function AppSidebarHeader() {
  return (
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" asChild>
            <Link href="/">
              <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                <EqualApproximately className="size-4" />
              </div>
              <div className="flex flex-col gap-0.5 leading-none">
                <span className="font-semibold">Zero</span>
              </div>
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  );
}

function AppSidebarContent({
  draft_invoices_count,
}: {
  draft_invoices_count: number;
}) {
  const { url } = usePage();

  const items = [
    {
      icon: ChartPieIcon,
      title: "Dashboard",
      url: "/",
      active: url === "/",
    },
    {
      icon: ListTodoIcon,
      title: "Reconciliations",
      url: "/reconciliations",
      active: url === "/reconciliations",
    },
    {
      icon: CreditCard,
      title: "Transactions",
      url: "/payment_transactions",
      active: url.startsWith("/payment_transactions"),
    },
    {
      icon: ReceiptText,
      title: "Invoices",
      url: "/invoices",
      active: url.startsWith("/invoices"),
    },
    {
      icon: ReceiptText,
      title: `Draft invoices (${draft_invoices_count})`,
      url: "/draft_invoices",
      active: url.startsWith("/draft_invoices"),
    },
    {
      icon: Layers,
      title: "Bank statements",
      url: "/bank_statements",
      active: url.startsWith("/bank_statements"),
    },
    {
      icon: Landmark,
      title: "Financial accounts",
      url: "/financial_accounts",
      active: url.startsWith("/financial_accounts"),
    },
    {
      icon: Users,
      title: "Merchants",
      url: "/merchants",
      active: url.startsWith("/merchants"),
    },
    {
      icon: Users,
      title: "Counterparties",
      url: "/counterparties",
      active: url.startsWith("/counterparties"),
    },
  ];

  return (
    <SidebarContent>
      <SidebarGroup>
        <SidebarMenu>
          {items.map((item) => {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  tooltip={item.title}
                  asChild
                  isActive={item.active}
                >
                  <Link href={item.url}>
                    <item.icon />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
  );
}

function AppSidebarFooter({ user }: { user: User }) {
  return (
    <SidebarFooter>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar className="h-8 w-8 rounded-lg">
                  {user.avatar && (
                    <AvatarImage src={user.avatar} alt={user.name} />
                  )}
                  <AvatarFallback className="rounded-lg">
                    {user.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side="top"
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    {user.avatar && (
                      <AvatarImage src={user.avatar} alt={user.name} />
                    )}
                    <AvatarFallback className="rounded-lg">
                      {user.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{user.name}</span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/profile">
                  <UserIcon />
                  Account
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link
                  href="/session"
                  method="delete"
                  as="button"
                  className="w-full"
                >
                  <LogOut />
                  Log out
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
}
